import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

/**
 * Recently Sent SMS — mini-table showing the last N SMS for the current SFA
 * (or globally if no SFA provided). Auto-refreshes every 5s while mounted.
 */
export default function RecentSmsTable({ sfaNumber, limit = 5, onResend }) {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const fetchRecent = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (sfaNumber) params.sfa_number = sfaNumber;
      const resp = await api.get("/aus/sms-recent", { params });
      setSends(resp.data?.sends || []);
    } catch (e) {
      // Quiet failure — keeps polling
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecent();
    timerRef.current = setInterval(fetchRecent, 5000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sfaNumber]);

  if (sends.length === 0) {
    return null;
  }

  return (
    <Card className="p-3 bg-white" data-testid="recent-sms-table">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Recently sent SMS</h4>
          <p className="text-[11px] text-muted-foreground">
            {sfaNumber ? `Filtered by ${sfaNumber}` : "Last 5 across all SFAs"} ·
            <span className="ml-1">refreshes every 5s</span>
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchRecent}
          disabled={loading}
          className="h-7 px-2"
          data-testid="recent-sms-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 px-2">Recipient</th>
              <th className="text-left py-1.5 px-2">Customer</th>
              <th className="text-left py-1.5 px-2">Sent</th>
              <th className="text-left py-1.5 px-2">Status</th>
              <th className="text-left py-1.5 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {sends.map((s, idx) => (
              <tr
                key={`${s.to}-${s.sent_at}-${idx}`}
                className="border-b last:border-0 hover:bg-slate-50"
                data-testid={`recent-sms-row-${idx}`}
              >
                <td className="py-1.5 px-2 font-mono">{s.to}</td>
                <td className="py-1.5 px-2 max-w-[160px] truncate" title={s.customer_name}>
                  {s.customer_name || "—"}
                </td>
                <td className="py-1.5 px-2 text-slate-600">{formatTime(s.sent_at)}</td>
                <td className="py-1.5 px-2">
                  <StatusBadge status={s.status} />
                </td>
                <td className="py-1.5 px-2">
                  {onResend && (
                    <button
                      type="button"
                      onClick={() => onResend(s)}
                      className="text-emerald-700 hover:underline text-[11px]"
                      data-testid={`recent-sms-resend-${idx}`}
                    >
                      Resend
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const cfg =
    {
      delivered: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Delivered ✓✓" },
      sent: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Sent ✓" },
      failed: { cls: "bg-red-100 text-red-800 border-red-300", label: "Failed ✗" },
      undelivered: { cls: "bg-red-100 text-red-800 border-red-300", label: "Undelivered ✗" },
    }[status] || { cls: "bg-slate-100 text-slate-700 border-slate-300", label: status || "—" };
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-semibold border ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return d.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}
